/*
 * VAJA · product page behaviour. Loaded (defer, classic script) on product pages only,
 * from snippets/stylesheets.liquid. No dependencies, ~16 KB (~5 KB gzipped).
 *
 * 1. Variant switch: VAJA blocks render one element per variant ([data-vaja-variant])
 *    inside a group ([data-vaja-variant-for="<product id>"]). On Horizon's
 *    `variant:update` event the matching element is shown, the others hidden.
 * 2. "Direct afrekenen" ([data-vaja-buy-now]): adds the selected variant + quantity
 *    from Horizon's product form to the cart (Ajax API) and opens /checkout, where
 *    the customer chooses a Mollie method (iDEAL | Wero, Bancontact, Klarna, cards).
 *    Look for "[vaja-buy-now]" in the console when debugging.
 * 3. Faster colour switch: when a colour dot is hovered, focused or touched, that
 *    variant's main photo is downloaded in the background with the same srcset/sizes
 *    as the gallery, so it comes from the browser cache when Horizon swaps the gallery.
 *    After the first interaction the other colours are warmed when the browser is idle.
 *    Nothing is loaded before the shopper touches the colour picker (no LCP impact),
 *    and nothing extra is loaded with Data Saver on. On click, the visible gallery photo
 *    is swapped to the chosen colour immediately, without waiting for Horizon's
 *    section fetch; Horizon's own update then shows the same (cached) photo.
 * 4. Faster thumbnails: on the same intent, Horizon's background page request for that
 *    colour is started early and kept for 60 s, and the new thumbnails are downloaded,
 *    so the thumbnail strip swaps without waiting. Other colours follow when idle.
 */
(() => {
  const root = () => window.Shopify?.routes?.root || '/';

  /** @param {string|number} productId @param {string|number} variantId @param {boolean|undefined} available */
  const showVariant = (productId, variantId, available) => {
    if (!variantId) return;
    const id = String(variantId);
    const groups = productId
      ? document.querySelectorAll(`[data-vaja-variant-for="${productId}"]`)
      : document.querySelectorAll('[data-vaja-variant-for]');
    groups.forEach((group) => {
      group.querySelectorAll('[data-vaja-variant]').forEach((el) => {
        el.hidden = el.getAttribute('data-vaja-variant') !== id || el.hasAttribute('data-vaja-empty');
      });
    });
    if (typeof available === 'boolean') {
      document
        .querySelectorAll(productId ? `[data-vaja-buy-now][data-product-id="${productId}"]` : '[data-vaja-buy-now]')
        .forEach((button) => {
          if (button instanceof HTMLButtonElement) button.disabled = !available;
        });
    }
  };

  document.addEventListener('variant:update', (event) => {
    const detail = /** @type {any} */ (event).detail || {};
    const variant = detail.resource;
    if (!variant || !variant.id) return;
    showVariant(detail.data?.productId, variant.id, variant.available);
  });

  document.addEventListener('click', async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-vaja-buy-now]') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    event.preventDefault();
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const scope = button.closest('.shopify-section') || document;
    const form = scope.querySelector('form[data-type="add-to-cart-form"]');
    const errorEl = button.parentElement?.querySelector('[data-vaja-buy-now-error]');
    const showError = (message) => {
      if (!(errorEl instanceof HTMLElement)) return;
      errorEl.textContent = message || errorEl.dataset.default || '';
      errorEl.hidden = false;
    };
    if (errorEl instanceof HTMLElement) errorEl.hidden = true;

    if (!(form instanceof HTMLFormElement)) {
      window.location.href = `${root()}cart`;
      return;
    }

    const data = new FormData(form);
    const id = Number(data.get('id'));
    const quantity = Math.max(1, Number(data.get('quantity')) || 1);
    if (!id) return showError();

    button.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id, quantity }] }),
      });
      if (response.ok) {
        window.location.href = `${root()}checkout`;
        return;
      }
      const body = await response.json().catch(() => null);
      console.warn('[vaja-buy-now] add rejected', response.status, body);
      showError(body?.description || body?.message);
    } catch (error) {
      console.error('[vaja-buy-now] request failed', error);
      showError();
    }
    button.removeAttribute('aria-busy');
  });

  /* 3. Preload a colour's photo on intent. */
  /** @type {Map<string, {srcset: string, sizes: string, src: string}>} */
  const warmed = new Map();
  const saveData = () => Boolean(/** @type {any} */ (navigator).connection?.saveData);

  /** The largest responsive image in the section = the gallery's main photo. */
  const mainImage = (scope) => {
    let best = null;
    let bestWidth = 0;
    scope.querySelectorAll('img[srcset]').forEach((img) => {
      const width = img.getBoundingClientRect().width;
      if (width > bestWidth) {
        bestWidth = width;
        best = img;
      }
    });
    return /** @type {HTMLImageElement|null} */ (best);
  };

  const imageMap = (scope) => {
    const node = scope.querySelector('script[data-vaja-variant-images]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      return {};
    }
  };

  const warmVariant = (scope, variantId) => {
    const id = String(variantId || '');
    if (!id || warmed.has(id)) return;
    const url = imageMap(scope)[id];
    if (!url) return;
    const entry = { srcset: '', sizes: '', src: url };
    warmed.set(id, entry);

    const preload = new Image();
    preload.decoding = 'async';
    const main = mainImage(scope);
    if (main && main.srcset) {
      const base = new URL(url, window.location.href);
      const srcset = main.srcset
        .split(',')
        .map((candidate) => {
          const [src, descriptor = ''] = candidate.trim().split(/\s+/);
          try {
            const current = new URL(src, window.location.href);
            const next = new URL(base.href);
            current.searchParams.forEach((value, key) => {
              if (key !== 'v') next.searchParams.set(key, value);
            });
            return `${next.href} ${descriptor}`.trim();
          } catch (error) {
            return '';
          }
        })
        .filter(Boolean)
        .join(', ');
      preload.sizes = main.sizes || '100vw';
      preload.srcset = srcset;
      entry.srcset = srcset;
      entry.sizes = preload.sizes;
    } else {
      preload.src = url;
    }
  };

  /** The gallery photo the shopper is looking at right now (largest visible responsive image). */
  const visibleMainImage = (scope) => {
    let best = null;
    let bestArea = 0;
    scope.querySelectorAll('img[srcset]').forEach((img) => {
      const rect = img.getBoundingClientRect();
      let left = rect.left;
      let right = rect.right;
      // Clip to the nearest scrolling/clipping ancestor (the gallery slider).
      for (let el = img.parentElement; el && el !== scope; el = el.parentElement) {
        const overflow = getComputedStyle(el).overflowX;
        if (overflow !== 'visible') {
          const box = el.getBoundingClientRect();
          left = Math.max(left, box.left);
          right = Math.min(right, box.right);
          break;
        }
      }
      const area = Math.max(0, right - left) * rect.height;
      if (area > bestArea) {
        bestArea = area;
        best = img;
      }
    });
    return /** @type {HTMLImageElement|null} */ (best);
  };

  /* Instant swap: show the chosen colour's photo straight away, before Horizon has
     fetched the new gallery. Horizon's update then lands on the same (cached) photo. */
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.variantId) return;
    const scope = input.closest('.shopify-section');
    if (!scope || !scope.querySelector('script[data-vaja-variant-images]')) return;
    warmVariant(scope, input.dataset.variantId);
    const entry = warmed.get(input.dataset.variantId);
    const img = visibleMainImage(scope);
    if (!entry || !img) return;
    img.removeAttribute('loading');
    if (entry.srcset) {
      if (entry.sizes) img.sizes = entry.sizes;
      img.srcset = entry.srcset;
    } else {
      img.removeAttribute('srcset');
    }
    img.src = entry.src;
  });

  const warmAll = (scope) => {
    if (saveData()) return;
    const run = () => Object.keys(imageMap(scope)).forEach((id) => warmVariant(scope, id));
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 2000 });
    else window.setTimeout(run, 400);
  };

  const onIntent = (event) => {
    const label = event.target instanceof Element ? event.target.closest('.variant-option__button-label') : null;
    if (!label) return;
    const input = label.querySelector('input[data-variant-id]');
    const scope = label.closest('.shopify-section');
    if (!(input instanceof HTMLInputElement) || !scope || !scope.querySelector('script[data-vaja-variant-images]')) return;
    warmVariant(scope, input.dataset.variantId);
    if (!scope.hasAttribute('data-vaja-warmed')) {
      scope.setAttribute('data-vaja-warmed', '');
      warmAll(scope);
    }
  };
  document.addEventListener('pointerover', onIntent, { passive: true });
  document.addEventListener('focusin', onIntent);
  document.addEventListener('touchstart', onIntent, { passive: true });

  /* 4. Faster thumbnails. Horizon reloads the product page in the background on every
     colour click and only then swaps the gallery (thumbnails included). On intent we
     start that same request early and keep the response for 60 s, so the click is
     served from memory; the new thumbnails are downloaded in the same go. After the
     first interaction the other colours are prepared one by one when the browser is
     idle (skipped with Data Saver or on 2G/3G). Nothing runs before the picker is used. */
  const pageCache = new Map();
  const PAGE_TTL = 60000;
  const nativeFetch = window.fetch.bind(window);
  const slowNetwork = () =>
    saveData() || /(^|-)(2g|3g)$/.test(String(/** @type {any} */ (navigator).connection?.effectiveType || ''));

  window.fetch = function (input, init) {
    const hit = typeof input === 'string' ? pageCache.get(input) : null;
    if (!hit || Date.now() - hit.time > PAGE_TTL) return nativeFetch(input, init);
    const signal = init && init.signal;
    return new Promise((resolve, reject) => {
      const abort = () => reject(new DOMException('Aborted', 'AbortError'));
      if (signal) {
        if (signal.aborted) return abort();
        signal.addEventListener('abort', abort, { once: true });
      }
      hit.promise.then(
        (response) => resolve(response.clone()),
        () => nativeFetch(input, init).then(resolve, reject)
      );
    });
  };

  /** Same URL Horizon's variant picker will request when this radio is chosen. */
  const pageUrlFor = (input) => {
    const picker = input.closest('variant-picker');
    if (!picker || typeof (/** @type {any} */ (picker).buildRequestUrl) !== 'function') return '';
    if (picker.closest('product-card, quick-add-dialog') || input.dataset.connectedProductUrl) return '';
    const fieldset = input.closest('fieldset');
    const ids = Array.from(picker.querySelectorAll('select option[selected], fieldset input:checked')).map(
      (el) => (el.closest('fieldset') === fieldset ? input : el).getAttribute('data-option-value-id')
    );
    if (!ids.length || ids.some((id) => !id)) return '';
    const url = /** @type {any} */ (picker).buildRequestUrl(input);
    return /option_values=[^&]*/.test(url) ? url.replace(/option_values=[^&]*/, `option_values=${ids.join(',')}`) : '';
  };

  /** Download the new thumbnails at the size the current ones use. */
  const warmThumbnails = (html, scope) => {
    const current = Array.from(scope.querySelectorAll('media-gallery slideshow-controls img')).find(
      (img) => img instanceof HTMLImageElement && img.currentSrc
    );
    if (!(current instanceof HTMLImageElement)) return;
    let width = '';
    try {
      width = new URL(current.currentSrc, window.location.href).searchParams.get('width') || '';
    } catch (error) {
      return;
    }
    if (!width) return;
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('media-gallery');
    if (!next) return;
    const seen = new Set();
    next.querySelectorAll('slideshow-controls img[srcset]').forEach((img) => {
      const match = (img.getAttribute('srcset') || '')
        .split(',')
        .map((part) => part.trim().split(/\s+/))
        .find(([, descriptor]) => descriptor === `${width}w`);
      if (!match || seen.has(match[0])) return;
      seen.add(match[0]);
      const preload = new Image();
      preload.decoding = 'async';
      preload.src = new URL(match[0], window.location.href).href;
    });
  };

  const prefetchPage = (input) => {
    const url = pageUrlFor(input);
    const cached = url ? pageCache.get(url) : null;
    if (!url || (cached && Date.now() - cached.time < PAGE_TTL)) return Promise.resolve();
    const promise = nativeFetch(url).then((response) => {
      if (!response.ok) throw new Error(String(response.status));
      return response;
    });
    pageCache.set(url, { promise, time: Date.now() });
    const scope = input.closest('.shopify-section');
    return promise
      .then((response) => response.clone().text())
      .then((html) => scope && warmThumbnails(html, scope))
      .catch(() => pageCache.delete(url));
  };

  const prefetchOthers = (picker) => {
    if (slowNetwork()) return;
    const queue = Array.from(picker.querySelectorAll('fieldset input[type="radio"]:not(:checked)'));
    const idle = (fn) =>
      'requestIdleCallback' in window ? window.requestIdleCallback(fn, { timeout: 3000 }) : window.setTimeout(fn, 500);
    const step = () => {
      const input = queue.shift();
      if (input) prefetchPage(input).then(() => idle(step));
    };
    idle(step);
  };

  const prefetchedProducts = new Set();
  let intentTimer = 0;
  const onPageIntent = (event, delay) => {
    const target = event.target instanceof Element ? event.target : null;
    const label = target?.closest('variant-picker .variant-option__button-label');
    const input = label?.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement) || input.checked || saveData()) return;
    window.clearTimeout(intentTimer);
    intentTimer = window.setTimeout(() => {
      prefetchPage(input);
      const picker = input.closest('variant-picker');
      const key = picker instanceof HTMLElement ? picker.dataset.productId || '' : '';
      if (picker && key && !prefetchedProducts.has(key)) {
        prefetchedProducts.add(key);
        prefetchOthers(picker);
      }
    }, delay);
  };
  document.addEventListener('pointerover', (event) => onPageIntent(event, 60), { passive: true });
  document.addEventListener('pointerdown', (event) => onPageIntent(event, 0), { passive: true });
  document.addEventListener('focusin', (event) => onPageIntent(event, 0));


  /* Back/forward cache: never leave the button stuck in the busy state. */
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-vaja-buy-now][aria-busy]').forEach((b) => b.removeAttribute('aria-busy'));
  });
})();
